<template>
  <div
    v-loading="loading"
    class="data-source-management"
    :class="{ 'data-source-management--init': isInit }"
  >
    <div class="data-source-management-inner">
      <div v-if="!isInit" class="data-source-management__title">数据源管理</div>
      <div v-else class="data-source-management__init-desc">
        以下是您已开通的数据服务，启用后 AI 助理将使用这些数据进行分析
      </div>
      <div class="data-source-management__content">
        <div
          v-for="item in filteredDataSourceList"
          :key="item.dataSourceType"
          class="data-source-management__card"
        >
          <div class="data-source-management__card-head">
            <div class="data-source-management__card-head-left">
              <div class="data-source-management__icon-wrap">
                <i class="t-iconfont" :class="getIconClass(item)"></i>
              </div>
              <div class="data-source-management__card-name">{{ item.dataSourceName }}</div>
            </div>
            <div class="data-source-management__card-head-right">
              <template v-if="item.dataSourceType === '1'">
                <div class="data-source-management__crm-box">
                  <div class="data-source-management__crm-icon-wrap">
                    <img alt="" :src="xbbLogo" />
                  </div>
                  <span class="data-source-management__crm-text">销帮帮 CRM</span>
                </div>
              </template>
              <template v-else-if="item.dataSourceType === '6'">
                <div class="data-source-management__device-link">
                  <span v-if="item.needPrompt === 1" class="desc">暂未分配设备给员工，请配置 </span
                  ><span class="main" @click="handleDeviceClick(item)">设备管理</span>
                </div>
              </template>
              <template v-else>
                <el-switch
                  v-model="item.enableStatus"
                  :active-value="1"
                  class="data-source-management__switch"
                  :disabled="+item.editable !== 1 || !!switchLoadingMap[item.dataSourceType]"
                  :inactive-value="0"
                  :loading="!!switchLoadingMap[item.dataSourceType]"
                  style="--el-switch-on-color: #7a52f4; --el-switch-off-color: #dcdfe6"
                  @change="(value) => handleEnableChange(item, value)"
                />
              </template>
            </div>
          </div>
          <div class="data-source-management__tags">
            <span
              v-for="detail in getDisplayDetails(item)"
              :key="detail"
              class="data-source-management__tag"
            >
              {{ detail }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <DeviceSet
      v-if="deviceSetVisible"
      v-model:visible="deviceSetVisible"
      :decice-tab="deciceTab"
    ></DeviceSet>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { getDataSourceList, editDataSource } from '@/api/ai-setting'
import DeviceSet from './components/device-set.vue'
import xbbLogo from '@/assets/xbb.png'

defineOptions({
  name: 'DataSourceManagement'
})

defineProps({
  isInit: {
    type: Boolean,
    default: false
  }
})

const loading = ref(false)
const dataSourceList = ref([])
const switchLoadingMap = ref({})
const deviceSetVisible = ref(false)
const deciceTab = ref([])

const dataSourceIconMap = {
  1: 'icon-CRM-line',
  2: 'icon-phone-line',
  3: 'icon-smartphone-line',
  4: 'icon-qiwei-line',
  5: 'icon-chat-voice-ai-line',
  6: 'icon-gongpai-ai-line'
}

const dataSourceDetailMap = {
  clue: '线索',
  customer: '客户',
  salesOpportunity: '销售机会',
  customerCommunicate: '跟进记录',
  contract: '合同',
  xbbCall: '销帮帮话机盒子',
  hkh: '好客呼',
  ykMobileCall: '云客手机（手机电话）',
  ykMobileWechatCall: '云客手机（微信电话）',
  ykMobileWechatRecord: '云客手机（微信聊天）',
  sessionArchive: '企微会话存档',
  listing: '智能挂牌',
  badge: '智能胸牌',
  bluetoothBadge: '智能贴片',
  larkGroup: '飞书群聊'
}

const getIconClass = (item) => dataSourceIconMap[item.dataSourceType] || 'icon-database-line'

const getDisplayDetails = (item) => {
  return item.dataSourceDetail.map((i) => dataSourceDetailMap[i])
}

const filteredDataSourceList = computed(() => {
  return dataSourceList.value.filter((item) => getDisplayDetails(item).length)
})

const handleEnableChange = async (item, value) => {
  const rollbackValue = Number(value) === 1 ? 0 : 1

  switchLoadingMap.value[item.dataSourceType] = true
  try {
    await editDataSource({
      dataSourceType: item.dataSourceType,
      enableStatus: Number(value)
    })
  } catch (error) {
    item.enableStatus = rollbackValue
    throw error
  } finally {
    switchLoadingMap.value[item.dataSourceType] = false
  }
}

const handleDeviceClick = (item) => {
  deciceTab.value = item.dataSourceDetail
  // 设备管理
  deviceSetVisible.value = true
}

const initData = async () => {
  loading.value = true
  try {
    const res = await getDataSourceList()
    dataSourceList.value = res.result?.dataSources || []
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  initData()
})
</script>

<style lang="scss" scoped>
.data-source-management {
  height: 100%;
  width: 100%;
  position: relative;
  overflow-y: auto;
  box-sizing: border-box;
  background: #fff;

  .data-source-management-inner {
    height: 100%;
    width: 852px;
    margin: 0 auto;
    padding: 48px 0 24px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
  }

  &--init {
    .data-source-management-inner {
      width: 100%;
      padding: 0;
    }
  }

  &__title {
    margin-bottom: 24px;
    font-size: 20px;
    line-height: 28px;
    font-weight: 600;
    color: #323233;
  }

  &__init-desc {
    margin-bottom: 16px;
    font-size: 14px;
    line-height: 20px;
    color: #969799;
  }

  &__content {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  &__card {
    padding: 16px;
    box-sizing: border-box;
    border: 1px solid #f2f3f5;
    border-radius: 16px;
    margin-bottom: 12px;
  }

  &__card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__card-head-left {
    min-width: 0;
    display: flex;
    align-items: center;
    flex: 1;
    margin-right: 12px;
  }

  &__icon-wrap {
    width: 32px;
    height: 32px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #f2f3f5;
    border-radius: 8px;
    flex: 0 0 auto;

    .t-iconfont {
      font-size: 20px;
      color: #646566;
    }
  }

  &__card-name {
    min-width: 0;
    margin-left: 12px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
    color: #323233;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__card-head-right {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
  }

  &__crm-box {
    width: 180px;
    height: 32px;
    box-sizing: border-box;
    padding: 6px;
    display: flex;
    align-items: center;
    border: 1px solid #e5e6eb;
    border-radius: 8px;
    background: #f7f8fa;
    cursor: not-allowed;
  }

  &__crm-icon-wrap {
    width: 20px;
    height: 20px;
    position: relative;
    img {
      width: 100%;
      height: 100%;
    }
  }

  &__crm-text {
    margin-left: 8px;
    font-size: 13px;
    line-height: 20px;
    font-weight: 500;
    color: #323233;
  }

  &__switch {
    flex: 0 0 auto;
  }

  &__device-link {
    font-size: 12px;
    line-height: 20px;
    color: #248df5;
    .desc {
      color: #969799;
    }
    .main {
      cursor: pointer;
    }
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    margin-top: 8px;
  }

  &__tag {
    padding: 4px 10px;
    box-sizing: border-box;
    border-radius: 8px;
    margin-right: 8px;
    background: #f7f8fa;
    font-size: 12px;
    line-height: 16px;
    color: #323233;
  }
}
</style>
